import { Divider, Typography } from "@material-ui/core"
import { AddCircle } from "@material-ui/icons"
import { uniq } from "lodash"
import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { useQuery } from "react-query"
import { listCourses } from "~/actions/courses"
import DarkButton from "~/components/Common/DarkButton"
import AppContext from "~/contexts/App"
import CertificationContext from "~/contexts/Certification"
import AddCourseDialogGeneric from "~/dialogs/AddCourseDialogGeneric"
import AddCourseCard from "~/dialogs/AddCourseDialogGeneric/CourseCard"
import AddPathDialog from "~/dialogs/AddPathDialog"

import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import Box from "../../Common/Box"
import AddedPathCard from "../AddedPathCard"
import NoPathAdded from "../NoPathAdded"
import useStyles from "./styles"

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const getLatest = (array) => {
  const ids = array.map((c) => c.container_id)
  const uniqContainerIds = uniq(ids)
  const uniqArray = uniqContainerIds.map((u) =>
    array.find((c) => c.container_id === u),
  )
  let newArr = []
  uniqArray.map((c) => {
    if (c.versions.find((v) => v.published === 1)) {
      const published = c.versions.find((v) => v.published === 1)
      newArr = [...newArr, published]
    } else {
      const draft = c.versions[c.versions.length - 1]
      newArr = [...newArr, draft]
    }
  })
  return newArr
}
function Course({ course, addedCourses, setAddedCourses, index }) {
  return (
    <Draggable draggableId={course.id.toString()} index={index}>
      {(provided) => {
        return (
          <AddCourseCard
            course={course}
            type="view"
            index={index}
            addedCourses={addedCourses}
            setAddedCourses={setAddedCourses}
            provided={provided}
          />
        )
      }}
    </Draggable>
  )
}

const CourseList = React.memo(function CourseList({
  courses,
  setAddedCourses,
}) {
  return courses?.map((course, index) => (
    <Course
      course={course}
      type="view"
      index={index}
      key={course.id}
      addedCourses={courses}
      setAddedCourses={setAddedCourses}
    />
  ))
})

const LearningPath = ({ path, index, lockedCertification, type }) => {
  return (
    <Draggable draggableId={path?.id.toString()} index={index}>
      {(provided) => {
        return (
          <AddedPathCard
            path={path}
            provided={provided}
            type={type}
            lockedCertification={lockedCertification}
          />
        )
      }}
    </Draggable>
  )
}

const LearningPathList = React.memo(function LearningPathList({
  paths,
  type,
  lockedCertification,
}) {
  return paths.map((path, index) => (
    <LearningPath
      path={path}
      index={index}
      key={path.id}
      type={type}
      lockedCertification={lockedCertification}
    />
  ))
})

const CertificationContent = () => {
  const classes = useStyles()
  const { openDialog } = useContext(AppContext)

  const {
    certification,
    attachedPaths,
    setAttachedPaths,
    setInitialAttachedPaths,
    beforeCertificationCourses,
    setBeforeCertificationCourses,
    afterCertificationCourses,
    setAfterCertificationCourses,
    setInitialBeforeCertificationCourses,
    setInitialAfterCertificationCourses,
  } = useContext(CertificationContext)

  const { data: courses } = useQuery("courses", () => listCourses())

  const addLearningPath = () => {
    openDialog("addPathDialog")
  }

  const addBeforeCourses = async () => {
    const newCourses = await openDialog(
      "addCourseDialogGeneric",
      beforeCertificationCourses,
    )
    if (newCourses !== null) {
      setBeforeCertificationCourses(newCourses)
    }
  }

  const addAfterCourses = async () => {
    const newCourses = await openDialog(
      "addCourseDialogGeneric",
      afterCertificationCourses,
    )
    if (newCourses !== null) {
      setAfterCertificationCourses(newCourses)
    }
  }

  //populate learning path array
  useEffect(() => {
    let newArray = []

    certification?.learning_paths?.map((path) => {
      if (path.id) {
        newArray.push(path)
      }
    })
    setAttachedPaths(newArray)
    setInitialAttachedPaths(newArray)
  }, [certification?.learning_paths])

  useEffect(() => {
    if (courses && courses.length > 0 && certification?.courses.length > 0) {
      const versionedCourses = getLatest(courses)
      const beforeCourses = certification?.courses
        .filter((bc) => bc.position === "before")
        .map((bc) => {
          return versionedCourses.find(
            (c) => c.container_id === bc.course_container_id,
          )
        })
      setInitialBeforeCertificationCourses(beforeCourses)
      setBeforeCertificationCourses(beforeCourses)
      const afterCourses = certification?.courses
        .filter((bc) => bc.position === "after")
        .map((bc) => {
          return versionedCourses.find(
            (c) => c.container_id === bc.course_container_id,
          )
        })
      setAfterCertificationCourses(afterCourses)
      setInitialAfterCertificationCourses(afterCourses)
    }
  }, [certification?.courses, courses])

  function onLearningPathDragEnd(result) {
    if (!result.destination) {
      return
    }

    if (result.destination.index === result.source.index) {
      return
    }

    const orderedPaths = reorder(
      attachedPaths,
      result.source.index,
      result.destination.index,
    )

    setAttachedPaths(orderedPaths)
  }

  function onBeforeCoursesDragEnd(result) {
    if (!result.destination) {
      return
    }

    if (result.destination.index === result.source.index) {
      return
    }

    const orderedBeforeCourses = reorder(
      beforeCertificationCourses,
      result.source.index,
      result.destination.index,
    )

    setBeforeCertificationCourses(orderedBeforeCourses)
  }

  function onAfterCoursesDragEnd(result) {
    if (!result.destination) {
      return
    }

    if (result.destination.index === result.source.index) {
      return
    }

    const orderedAfterCourses = reorder(
      afterCertificationCourses,
      result.source.index,
      result.destination.index,
    )

    setAfterCertificationCourses(orderedAfterCourses)
  }

  const lockedCertification =
    certification?.published ||
    (!certification?.published && certification.has_been_published)

  const renderCertificationCourses = () => {
    return (
      <>
        {/* Before */}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              Before Certification Courses
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          <PermissionsWrapper permissionGroup="admin">
            <DarkButton
              onClick={addBeforeCourses}
              // style={path?.published ? { display: "none" } : null}
              startIcon={
                <AddCircle
                  fontSize="small"
                  classes={{ root: classes.addCircle }}
                />
              }
            >
              Add Course
            </DarkButton>
          </PermissionsWrapper>
        </Box>
        <Box marginLeft="37px" marginRight="37px" paddingBottom="16px">
          {beforeCertificationCourses?.length > 0 ? (
            <DragDropContext onDragEnd={onBeforeCoursesDragEnd}>
              <Droppable droppableId="course">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <CourseList
                      courses={beforeCertificationCourses}
                      setAddedCourses={setBeforeCertificationCourses}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : null}
        </Box>

        {/* After */}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              After Certification Courses{" "}
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          <PermissionsWrapper permissionGroup="admin">
            <DarkButton
              onClick={addAfterCourses}
              // style={path?.published ? { display: "none" } : null}
              startIcon={
                <AddCircle
                  fontSize="small"
                  classes={{ root: classes.addCircle }}
                />
              }
            >
              Add Course
            </DarkButton>
          </PermissionsWrapper>
        </Box>
        <Box marginLeft="37px" marginRight="37px" paddingBottom="32px">
          {afterCertificationCourses?.length > 0 ? (
            <DragDropContext onDragEnd={onAfterCoursesDragEnd}>
              <Droppable droppableId="course">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <CourseList
                      courses={afterCertificationCourses}
                      setAddedCourses={setAfterCertificationCourses}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : null}
        </Box>
        <AddCourseDialogGeneric />
      </>
    )
  }

  return (
    <Box position="relative">
      <Box minHeight="100%">
        {lockedCertification ? (
          <Box className={classes.publishedBanner}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography className={classes.title} variant="h3">
                You are in a{" "}
                {certification?.published ? "published" : "unpublished"}{" "}
                certification, this cannot be updated
              </Typography>
            </Box>
          </Box>
        ) : null}

        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
        >
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            minHeight="37px"
          >
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              Learning Paths{" "}
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          {attachedPaths?.length > 0 && (
            <PermissionsWrapper permissionGroup="admin">
              <DarkButton
                onClick={() => addLearningPath()}
                startIcon={
                  <AddCircle
                    fontSize="small"
                    classes={{ root: classes.addCircle }}
                  />
                }
                style={certification?.published ? { display: "none" } : null}
              >
                Add Learning Path
              </DarkButton>
            </PermissionsWrapper>
          )}
        </Box>
        <Box marginLeft="37px" marginRight="37px">
          {attachedPaths?.length > 0 ? (
            <DragDropContext onDragEnd={onLearningPathDragEnd}>
              <Droppable droppableId="path">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <LearningPathList
                      paths={attachedPaths}
                      lockedCertification={lockedCertification}
                      type="view"
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <NoPathAdded onClick={() => addLearningPath()} />
          )}
        </Box>
        {lockedCertification ? (
          <Box className={classes.publishedOverlay} />
        ) : null}
        <AddPathDialog />
      </Box>

      <Divider
        style={{
          backgroundColor: "#000",
          opacity: "0.1",
          marginTop: 35,
        }}
      />
      {renderCertificationCourses()}
    </Box>
  )
}

Course.propTypes = {
  course: PropTypes.object,
  index: PropTypes.number,
  addedCourses: PropTypes.array,
  setAddedCourses: PropTypes.func,
}

CourseList.propTypes = {
  courses: PropTypes.array,
  setAddedCourses: PropTypes.func,
}

LearningPath.propTypes = {
  path: PropTypes.object,
  index: PropTypes.number,
  lockedCertification: PropTypes.number,
  type: PropTypes.string,
}

LearningPathList.propTypes = {
  paths: PropTypes.array,
  type: PropTypes.string,
  lockedCertification: PropTypes.number,
}

CertificationContent.propTypes = {
  lesson: PropTypes.object,
  index: PropTypes.number,
}
export default CertificationContent
